import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { readFiles } from '@shared/fileReaderUtils';
import { filterMap } from '@shared/utils/rxjs';
import { catchError, defer, from, map, mergeMap, Observable, of, throwError, toArray } from 'rxjs';
import { ExcelFileTransfer, xlsxImportError } from '../file-transfer-services/excelFileTransfer.service';
import { IFileDataSchema, IImportedData } from '../file-transfer-services/interfaces';
import { IRack, IResponseRack, RackingService, WorkCenterFilterMode } from '../racking.service';
import { FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, ITransferService, SuccessFullImport } from './interfaces';

const sheetName = 'Racks';
const importConcurrency = 5;

/** The only component types the create/edit dialog offers - import rejects anything else the same way. */
const ALLOWED_COMPONENTS = ['frame', 'sash', 'jambs', 'muntin'];

const NAME_PATTERN = /^[0-9a-zA-ZÆØÅæøå\s-_]*$/;
const DESCRIPTION_PATTERN = /^[0-9a-zA-ZÆØÅæøå.,\s]*$/;
const INT32_MAX = 2147483647;

export interface IRackExportData {
	name: string;
	description?: string;
	rows: number;
	columns: number;
	width?: number;
	height?: number;
	depth?: number;
	capacity: number;
	components: string[];
	workCenterFilterMode?: WorkCenterFilterMode;
	workCenterFilterKeys?: string[];
}

const RackSchema: IFileDataSchema<IRackExportData> = {
	name: sheetName,
	properties: {
		name: { type: 'string', required: true, fileKey: 'Name', width: 30 },
		description: { type: 'string', required: false, fileKey: 'Description', width: 50 },
		rows: { type: 'number', required: true, fileKey: 'Rows', width: 15 },
		columns: { type: 'number', required: true, fileKey: 'Columns', width: 15 },
		width: { type: 'number', required: false, fileKey: 'Width', width: 15 },
		height: { type: 'number', required: false, fileKey: 'Height', width: 15 },
		depth: { type: 'number', required: false, fileKey: 'Depth', width: 15 },
		capacity: { type: 'number', required: true, fileKey: 'Capacity', width: 15 },
		components: { type: { array: 'string' }, required: true, fileKey: 'Components', width: 30 },
		workCenterFilterMode: { type: 'string', required: false, fileKey: 'WorkCenterFilterMode', width: 25 },
		workCenterFilterKeys: { type: { array: 'string' }, required: false, fileKey: 'WorkCenterFilterKeys', width: 40 },
	},
};

interface RackImportContext {
	rackNameMap: Map<string, IResponseRack>;
}

/** A row that passed validation, carrying its file location through to a save failure too. */
interface RackImportCandidate {
	rack: IRack;
	location?: string;
}

@Injectable({
	providedIn: 'root',
})
export class RackTransferService implements ITransferService<IResponseRack> {
	private translationPrefix = 'masterdata.racking.rackImportExport';

	constructor(
		private rackingService: RackingService,
		private excelFileTransfer: ExcelFileTransfer<IRackExportData>,
		private translator: TranslateService,
	) {}

	public hasTemplate = () => true;
	public getTemplate = (): void => {
		this.downloadTemplate();
	};

	// Rack name uniqueness is not enforced anywhere on the frontend - neither the create/edit
	// dialog nor this list page pre-check it, it is only rejected by the backend on save. Nothing
	// here treats name as unique on faith: both the file and the existing roster are checked.
	public exportData(racks: IResponseRack[]): Observable<IResponseRack[]> {
		const duplicateNames = findDuplicateNames(racks);
		if (duplicateNames.length > 0) {
			return throwError(
				() =>
					new Error(
						this.translator.instant(`${this.translationPrefix}.export.errors.duplicate-names.content`, {
							names: duplicateNames.join(', '),
						}),
					),
			);
		}

		const data = racks.map((rack) => this.toExportRow(rack));

		// ExcelFileTransfer.exportData writes the file (and validates cell lengths) synchronously
		// before returning its observable, so calling it directly here would let a real write
		// failure throw past catchError below instead of reaching it - defer() delays that call
		// until subscription, inside RxJS's own error handling.
		return defer(() => this.excelFileTransfer.exportData(sheetName, data, RackSchema)).pipe(
			map(() => racks),
			catchError((error) => {
				throw new Error(`Failed to export data: ${error.message}`);
			}),
		);
	}

	// A blank starting point for import: the same fields exportData produces, with one filled-in
	// example row instead of real data.
	public downloadTemplate(): void {
		const exampleRow: IRackExportData = {
			name: 'Example Rack',
			description: 'Example description',
			rows: 5,
			columns: 10,
			width: 100,
			height: 100,
			depth: 100,
			capacity: 50,
			components: ['frame', 'sash'],
			workCenterFilterMode: WorkCenterFilterMode.Ignore,
			workCenterFilterKeys: [],
		};

		this.excelFileTransfer.exportData(`${sheetName}Template`, [exampleRow], RackSchema).subscribe();
	}

	private toExportRow(rack: IResponseRack): IRackExportData {
		return {
			name: rack.name,
			description: rack.description || '',
			rows: rack.configuration.rows,
			columns: rack.configuration.columns,
			width: rack.configuration.cell.width,
			height: rack.configuration.cell.height,
			depth: rack.configuration.cell.depth,
			capacity: rack.capacity.maximum,
			components: rack.components.map((component) => this.rackingService.getLastSegment(component.type.self)),
			workCenterFilterMode: rack.workCenters?.mode ?? WorkCenterFilterMode.Ignore,
			workCenterFilterKeys: rack.workCenters?.keys ?? [],
		};
	}

	// Duplicate names (file or roster) and per-row validation problems all get discovered and
	// reported together in one pass, instead of a user fixing one category, re-importing, and only
	// then discovering the next. Validating a row only needs the row itself, not unambiguous name
	// matching, so it's safe to run even when names collide - only the actual create/update is
	// withheld then, since it's the one step that depends on matching a row to exactly one existing
	// rack by name.
	public importData(file: File | File[]): Observable<ImportResponse> {
		const ImportErrors: ImportErrorTypes[] = [];
		const files = Array.isArray(file) ? file : [file];

		return readFiles(files, 'binary').pipe(
			mergeMap(this.importFileData(ImportErrors)),
			toArray(),
			mergeMap((parsedRows) => {
				// Nothing parsed - whatever went wrong is already in ImportErrors. Stop here instead
				// of fetching the roster only to report an unrelated duplicate-name error that would
				// bury the actual parse failure.
				if (parsedRows.length === 0) {
					return of({ successfulImports: [], errors: ImportErrors } as ImportResponse);
				}

				const fileDuplicates = findDuplicateNames(parsedRows.map((imported) => imported.data));

				return this.rackingService.getRacks().pipe(
					mergeMap((racks) => {
						const rosterDuplicates = findDuplicateNames(racks.items);

						if (fileDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-file', fileDuplicates);
						}
						if (rosterDuplicates.length > 0) {
							this.pushDuplicateNamesError(ImportErrors, 'duplicate-names-in-roster', rosterDuplicates);
						}
						const namesAreAmbiguous = fileDuplicates.length > 0 || rosterDuplicates.length > 0;

						const ctx: RackImportContext = {
							rackNameMap: buildRackNameMap(racks.items),
						};

						return from(parsedRows).pipe(
							filterMap(this.validateRow(ImportErrors)),
							toArray(),
							mergeMap((validRows) => (namesAreAmbiguous ? of([]) : from(validRows).pipe(mergeMap(this.createOrUpdateRack(ctx, ImportErrors), importConcurrency), toArray()))),
							map(
								(results) =>
									({
										successfulImports: results.map(
											(r) =>
												({
													Id: r.entity.name,
													status: r.status,
												}) as SuccessFullImport,
										),
										errors: ImportErrors,
									}) as ImportResponse,
							),
						);
					}),
				);
			}),
		);
	}

	private importFileData(ImportErrors: ImportErrorTypes[]) {
		return (buffer: ArrayBuffer): Observable<IImportedData<IRackExportData>> => {
			const errorList: xlsxImportError[] = [];

			try {
				const rows = this.excelFileTransfer.importData(buffer, RackSchema, errorList);

				errorList.forEach((e) => {
					ImportErrors.push({
						message: e.error,
						status: ImportStatus.ParsingError,
					} as FailedParse);
				});

				return from(rows);
			} catch (error) {
				ImportErrors.push({
					status: ImportStatus.ParsingError,
					message: error.message,
				} as FailedParse);
				return of();
			}
		};
	}

	// Mirrors every validator the create/edit dialog applies to its form (name/description
	// patterns and lengths, rows/columns/capacity/cell-dimension ranges, the closed component-type
	// list, and the work-center filter's own rules), since import is a second, dialog-bypassing
	// entry point into the same data.
	private validateRow(ImportErrors: ImportErrorTypes[]) {
		return (imported: IImportedData<IRackExportData>): RackImportCandidate | undefined => {
			const { data: row, location } = imported;
			const errors: Record<string, ValidationErrors> = {};

			const name = (row.name ?? '').trim();
			if (!name) {
				errors['name'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-name`),
				};
			} else if (name.length > 50) {
				errors['name'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.name-too-long`),
				};
			} else if (!NAME_PATTERN.test(name)) {
				errors['name'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.name-invalid-characters`),
				};
			}

			const description = (row.description ?? '').trim();
			if (description.length > 100) {
				errors['description'] = {
					maxlength: this.translator.instant(`${this.translationPrefix}.import.errors.description-too-long`),
				};
			} else if (description && !DESCRIPTION_PATTERN.test(description)) {
				errors['description'] = {
					pattern: this.translator.instant(`${this.translationPrefix}.import.errors.description-invalid-characters`),
				};
			}

			this.validateRange(errors, 'rows', row.rows, 0, 100, true);
			this.validateRange(errors, 'columns', row.columns, 0, 100, true);
			this.validateRange(errors, 'width', row.width, 0, INT32_MAX, false);
			this.validateRange(errors, 'height', row.height, 0, INT32_MAX, false);
			this.validateRange(errors, 'depth', row.depth, 0, INT32_MAX, false);
			this.validateRange(errors, 'capacity', row.capacity, 0, INT32_MAX, true);

			const components = row.components ?? [];
			if (components.length === 0) {
				errors['components'] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.missing-components`),
				};
			} else {
				const invalidComponents = components.filter((c) => !ALLOWED_COMPONENTS.includes(c));
				if (invalidComponents.length > 0) {
					errors['components'] = {
						invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-components`, {
							components: invalidComponents.join(', '),
						}),
					};
				}
			}

			const rawMode = row.workCenterFilterMode;
			const validModes: string[] = Object.values(WorkCenterFilterMode);
			if (rawMode && !validModes.includes(rawMode)) {
				errors['workCenterFilterMode'] = {
					invalid: this.translator.instant(`${this.translationPrefix}.import.errors.invalid-work-center-filter-mode`, {
						value: rawMode,
					}),
				};
			}

			const mode = rawMode && validModes.includes(rawMode) ? rawMode : WorkCenterFilterMode.Ignore;
			const keys = row.workCenterFilterKeys ?? [];
			const keyError = validateWorkCenterKeys(keys);
			if (keyError) {
				errors['workCenterFilterKeys'] = {
					[keyError]: this.translator.instant(`${this.translationPrefix}.import.errors.${keyError}`),
				};
			} else if (mode !== WorkCenterFilterMode.Ignore && keys.length === 0) {
				errors['workCenterFilterKeys'] = {
					empty: this.translator.instant(`${this.translationPrefix}.import.errors.work-center-filter-empty`),
				};
			}

			if (Object.keys(errors).length > 0) {
				ImportErrors.push({
					Id: name || '',
					location,
					status: ImportStatus.ValidationError,
					errors,
				} as FailedValidationImport);
				return undefined;
			}

			// The API collapses a mode without keys to no filter at all - see toRequestRack in
			// racking.service.ts - so the same collapse is applied here rather than sending Ignore
			// alongside a leftover key list.
			const hasFilter = mode !== WorkCenterFilterMode.Ignore && keys.length > 0;

			const rack: IRack = {
				self: null,
				name,
				description: description || '',
				updatedDate: null,
				updatedBy: null,
				rows: row.rows,
				columns: row.columns,
				components,
				translationComponents: null,
				width: row.width ?? null,
				height: row.height ?? null,
				depth: row.depth ?? null,
				capacity: row.capacity,
				workCenterFilterMode: hasFilter ? mode : WorkCenterFilterMode.Ignore,
				workCenterFilterKeys: hasFilter ? keys : [],
			};

			return { rack, location };
		};
	}

	private validateRange(errors: Record<string, ValidationErrors>, field: string, value: number | undefined, min: number, max: number, required: boolean): void {
		if (value === undefined || value === null) {
			if (required) {
				errors[field] = {
					required: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKeyForField(field)}`, {
						min,
						max,
					}),
				};
			}
			return;
		}

		if (!Number.isFinite(value) || !Number.isInteger(value) || value < min || value > max) {
			errors[field] = {
				range: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKeyForField(field)}`, {
					min,
					max,
				}),
			};
		}
	}

	private createOrUpdateRack(ctx: RackImportContext, ImportErrors: ImportErrorTypes[]) {
		return (candidate: RackImportCandidate) => {
			const { rack, location } = candidate;
			// Safe: importData() has already confirmed neither the file nor the roster has a name
			// collision, so at most one existing rack can match here.
			const existingRack = ctx.rackNameMap.get(normalizeName(rack.name));

			if (existingRack) {
				rack.self = existingRack.self;
				return this.rackingService.updateRack(rack).pipe(
					map(() => ({ entity: rack, status: ImportStatus.SuccessUpdate })),
					catchError((error) => {
						this.pushSaveFailedError(ImportErrors, rack, location, error);
						return of();
					}),
				);
			}

			return this.rackingService.createRack(rack).pipe(
				map(() => ({ entity: rack, status: ImportStatus.SuccessCreate })),
				catchError((error) => {
					this.pushSaveFailedError(ImportErrors, rack, location, error);
					return of();
				}),
			);
		};
	}

	// Never surface the backend's raw error text - map it to a translated, user-facing message
	// instead. Anything we haven't special-cased falls back to a generic "failed to save" message.
	private saveFailedErrorKey(error: unknown): string {
		switch ((error as HttpErrorResponse)?.status) {
			case 400:
				return 'save-failed-bad-request';
			case 404:
				return 'save-failed-not-found';
			default:
				return 'save-failed';
		}
	}

	private pushSaveFailedError(ImportErrors: ImportErrorTypes[], rack: IRack, location: string | undefined, error: unknown) {
		ImportErrors.push({
			Id: rack.name,
			location,
			status: ImportStatus.Failed,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${this.saveFailedErrorKey(error)}`, {
				name: rack.name,
			}),
		});
	}

	private pushDuplicateNamesError(ImportErrors: ImportErrorTypes[], errorKey: 'duplicate-names-in-file' | 'duplicate-names-in-roster', names: string[]): void {
		ImportErrors.push({
			status: ImportStatus.ParsingError,
			message: this.translator.instant(`${this.translationPrefix}.import.errors.${errorKey}`, {
				names: names.join(', '),
			}),
		} as FailedParse);
	}
}

function errorKeyForField(field: string): string {
	return `invalid-${field}`;
}

/**
 * Refuses entries the rack could never match on, mirroring validateWorkCenterEntry in racking-create-update-rack.ts: duplicates are the only rule the API itself rejects, the rest come from the profile side, where a profile's work centres are read out of a comma-separated, trimmed aggregate and compared ordinally. A
 * key carrying a comma or padding therefore matches nothing - and "A1" and "a1" are two distinct work centres, so the duplicate check is case-sensitive.
 */
function validateWorkCenterKeys(keys: string[]): 'work-center-whitespace' | 'work-center-comma' | 'work-center-padded' | 'work-center-duplicate' | null {
	const seen = new Set<string>();
	for (const key of keys) {
		if (key.trim() === '') {
			return 'work-center-whitespace';
		}
		if (key.includes(',')) {
			return 'work-center-comma';
		}
		if (key !== key.trim()) {
			return 'work-center-padded';
		}
		if (seen.has(key)) {
			return 'work-center-duplicate';
		}
		seen.add(key);
	}
	return null;
}

function buildRackNameMap(racks: IResponseRack[]): Map<string, IResponseRack> {
	const map = new Map<string, IResponseRack>();
	racks.forEach((rack) => map.set(normalizeName(rack.name), rack));
	return map;
}

/** Names that appear 2+ times once trimmed/lowercased. Blank names are skipped - a missing name is reported separately as a missing-required-field error, not a collision. Returns the original (non-normalized) spelling of every row involved in a collision, for a message a user can act on. */
function findDuplicateNames(items: { name: string }[]): string[] {
	const firstSeen = new Map<string, string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		const normalized = normalizeName(item.name);
		if (!normalized) {
			continue;
		}

		const original = firstSeen.get(normalized);
		if (original !== undefined) {
			duplicates.add(original);
			duplicates.add(item.name);
		} else {
			firstSeen.set(normalized, item.name);
		}
	}

	return Array.from(duplicates);
}

function normalizeName(name: unknown): string {
	return name === null || name === undefined ? '' : String(name).trim().toLowerCase();
}
